import { DollarSign, Receipt, Wallet, TrendingUp, Percent, CircleDollarSign } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/earningsCalculations';

/**
 * Financial Summary Card for Earnings Page
 *
 * Displays key financial metrics in a compact horizontal card.
 * Updates dynamically based on filters or row selection.
 */
export default function FinancialSummaryCard({
  grossRevenue = 0,
  gstCollected = 0,
  totalPayouts = 0,
  gstPaid = 0,
  netProfit = 0,
  profitMargin = 0,
  contextLabel = 'All Time',
  instanceCount = 0,
  numberFormat = 'en-AU'
}) {
  const metrics = [
    {
      label: 'Revenue',
      value: grossRevenue,
      icon: DollarSign,
      format: 'currency',
      color: 'text-blue-400'
    },
    {
      label: 'GST Collected',
      value: gstCollected,
      icon: Receipt,
      format: 'currency',
      color: 'text-cyan-400'
    },
    {
      label: 'Payouts',
      value: totalPayouts,
      icon: Wallet,
      format: 'currency',
      color: 'text-orange-400'
    },
    {
      label: 'GST Paid',
      value: gstPaid,
      icon: CircleDollarSign,
      format: 'currency',
      color: 'text-amber-400'
    },
    {
      label: 'Net Profit',
      value: netProfit,
      icon: TrendingUp,
      format: 'currency',
      color: netProfit >= 0 ? 'text-green-400' : 'text-red-400'
    },
    {
      label: 'Margin',
      value: profitMargin,
      icon: Percent,
      format: 'percent',
      color: profitMargin >= 0 ? 'text-green-400' : 'text-red-400'
    }
  ];

  const formatValue = (value, format) => {
    if (format === 'currency') {
      return formatCurrency(value, numberFormat);
    }
    if (format === 'percent') {
      return `${value.toFixed(1)}%`;
    }
    return value;
  };

  return (
    <div className="bg-surface border border-border rounded-lg px-4 py-3">
      {/* Context indicator */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            {contextLabel}
          </span>
          <span className="text-xs text-text-tertiary">
            • {instanceCount} {instanceCount === 1 ? 'instance' : 'instances'}
          </span>
        </div>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-6 gap-4">
        {metrics.map(({ label, value, icon: Icon, format, color }) => (
          <div key={label} className="flex flex-col">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Icon className={cn('w-3.5 h-3.5', color)} />
              <span className="text-xs text-text-tertiary">{label}</span>
            </div>
            <span className={cn(
              'text-sm font-semibold',
              format === 'currency' || format === 'percent'
                ? (label === 'Net Profit' || label === 'Margin'
                    ? (value >= 0 ? 'text-green-400' : 'text-red-400')
                    : 'text-text-primary')
                : 'text-text-primary'
            )}>
              {formatValue(value, format)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
